import * as React from 'react';
import {useDispatch, useSelector} from 'react-redux';
import useLabelTranslation from 'src/hooks/useLabelTranslation';
import {ERROR_CODES} from 'src/components/authentication/errors';

import {useEnvironmentContext} from 'src/hooks/useEnvironmentContext';
import {
  selectUserLoginId,
  selectUserEmail,
  selectUserNewPhoneNumber,
  selectCurrentAgency,
  selectOtpVerificationError,
  selectResetMfaDeviceError,
  selectOtpLimitExceeded,
  selectUserPhoneNumber,
  selectShouldEnrollTotp,
} from 'src/selectors/authentication';
import {
  TitleMedium,
  BodyLarge,
  SubTitleMedium,
  BodySmall,
} from '@spaced-out/ui-design-system/lib/components/Text';
import {
  verifyOtpForUser,
  performSignIn,
  resetMfaDevice,
} from 'src/action-creators/authentication';
import {AuthFlow} from 'src/components/authentication/auth-flow';
import {Input} from '@spaced-out/ui-design-system/lib/components/Input';
import {Button} from '@spaced-out/ui-design-system/lib/components/Button';
import {Link} from '@spaced-out/ui-design-system/lib/components/Link';
import {InContextAlert} from '@spaced-out/ui-design-system/lib/components/InContextAlert';
import classify from 'src/utils/classify';
import css from './authenticator-otp-verification.css';


type Props = {
  goToPage: (page: string, newMfaFlow?: string | null | undefined) => void;
};

const AuthenticatorOtpVerification = ({goToPage}: Props) => {
  const {makeKey, t} = useLabelTranslation();
  const {isExtension} = useEnvironmentContext();
  const [value, setValue] = React.useState('');

  const userEmail = useSelector(selectUserEmail);
  const loginId = useSelector(selectUserLoginId);
  const newPhoneNumber = useSelector(selectUserNewPhoneNumber);
  const phoneNumber = useSelector(selectUserPhoneNumber);
  const agency = useSelector(selectCurrentAgency);
  const dispatch = useDispatch();
  const mfaResetError = useSelector(selectResetMfaDeviceError);
  const apiError = useSelector(selectOtpVerificationError);
  const {
    error_type: errorType,
    pending_attempts,
    error: otpError,
  } = apiError || {};
  const isOtpLimitError = errorType === 'IncorrectOTP';

  const otpLimitErrorText =
    'Invalid verification code : {{pending_attempts}} attempts left';

  const otpLimitExceeded = useSelector(selectOtpLimitExceeded);
  const disableVerify = !value || value.length !== 6 || otpLimitExceeded;
  const shouldEnrollTotp = useSelector(selectShouldEnrollTotp);
  const [disabled, setDisabled] = React.useState(disableVerify);

  const handleVerifyOtp = async () => {
    setDisabled(true);
    const apiData = await dispatch(
      verifyOtpForUser(value, userEmail, loginId, newPhoneNumber, 'TOTP', true),
    );
    if ((phoneNumber || !shouldEnrollTotp) && apiData) {
      await dispatch(performSignIn(agency));
      // @ts-expect-error - TS2774 - This condition will always return true since this function is always defined. Did you mean to call it instead?
    } else if (apiData && !phoneNumber) {
      goToPage(AuthFlow.SMS_REGISTRATION.PHONE_REGISTRATION);
    }
    if (mfaResetError || otpError) {
      setDisabled(disableVerify);
    }
  };

  const handleResetDevice = async () => {
    await dispatch(resetMfaDevice(userEmail));
    if (!mfaResetError) {
      goToPage(AuthFlow.TOTP.RESET_DEVICE);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      handleVerifyOtp();
    }
  };

  const handleOtpChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    setValue(val);
    if (val && val.length === 6) {
      setDisabled(false);
    } else {
      setDisabled(true);
    }
  };
  return (
    <div className={css.container}>
      <div
        className={classify(css.title, {
          [css.textCenterAlign]: isExtension,
        })}
      >
        {isExtension ? (
          <SubTitleMedium
            className={classify({[css.centerAlign]: isExtension})}
          >
            {t('Multifactor_authentication', 'Multifactor authentication')}
          </SubTitleMedium>
        ) : (
          <TitleMedium className={classify({[css.centerAlign]: isExtension})}>
            {t('Multifactor_authentication', 'Multifactor authentication')}
          </TitleMedium>
        )}
        {isExtension ? (
          <BodySmall color="secondary">
            {t(
              'The_verification_code_has_been_sent_to_your_authenticator_app_Enter_the_code_below',
              'The verification code has been sent to your authenticator app. Enter the code below.',
            )}
          </BodySmall>
        ) : (
          <BodyLarge color="secondary">
            {t(
              'The_verification_code_has_been_sent_to_your_authenticator_app_Enter_the_code_below',
              'The verification code has been sent to your authenticator app. Enter the code below.',
            )}
          </BodyLarge>
        )}
      </div>
      <Input
        label={t('Enter_verification_code', 'Enter verification code')}
        size={isExtension ? 'small' : 'medium'}
        type="number"
        value={value}
        onChange={handleOtpChange}
        onKeyDown={handleKeyDown}
        error={!!otpError}
        errorText={
          isOtpLimitError
            ? t(makeKey(otpLimitErrorText), {
                pending_attempts,
                // @ts-expect-error - TS7053 - Element implicitly has an 'any' type because expression of type 'string' can't be used to index type '{ readonly InvalidLoginAttempt: "Incorrect email or password"; readonly ServerError: "Failed to perform action, Please try after sometime"; readonly IncorrectOTP: "Invalid OTP entered"; ... 13 more ...; readonly InvalidReferrerEmail: "Invalid Referrer Email"; }'.
                defaultValue: ERROR_CODES[otpError] || otpError,
              })
            : t(
                // @ts-expect-error - TS7053 - Element implicitly has an 'any' type because expression of type 'string' can't be used to index type '{ readonly InvalidLoginAttempt: "Incorrect email or password"; readonly ServerError: "Failed to perform action, Please try after sometime"; readonly IncorrectOTP: "Invalid OTP entered"; ... 13 more ...; readonly InvalidReferrerEmail: "Invalid Referrer Email"; }'.
                makeKey(ERROR_CODES[otpError] || otpError),
                // @ts-expect-error - TS7053 - Element implicitly has an 'any' type because expression of type 'string' can't be used to index type '{ readonly InvalidLoginAttempt: "Incorrect email or password"; readonly ServerError: "Failed to perform action, Please try after sometime"; readonly IncorrectOTP: "Invalid OTP entered"; ... 13 more ...; readonly InvalidReferrerEmail: "Invalid Referrer Email"; }'.
                ERROR_CODES[otpError] || otpError,
              )
        }
      />
      <Button
        onClick={handleVerifyOtp}
        size={isExtension ? 'small' : 'medium'}
        type="primary"
        disabled={disabled}
      >
        {t('Verify', 'Verify')}
      </Button>

      <Link
        onClick={handleResetDevice}
        as={isExtension ? 'buttonTextExtraSmall' : 'buttonTextMedium'}
        className={classify({[css.centerAlign]: isExtension})}
      >
        {t('Reset', 'Reset')}
      </Link>
      {mfaResetError && (
        <InContextAlert semantic="danger">
          {t(makeKey(mfaResetError), mfaResetError)}
        </InContextAlert>
      )}
    </div>
  );
};

export default AuthenticatorOtpVerification;
